<script lang="ts">
  import type {Video} from '@/types/project';
  import Swal from 'sweetalert2';
  import {onMount} from 'svelte';
  import {formatDate, randomString} from '@/utils/common';
  import {RecapService} from '@/services/';
  import type {RecapProject} from '@/store/recap';
  import {recap, step} from '@/store';
  import {STEP} from '@/types/common';

  export let selectedVideo: Video | null;

  let openDropdownIndex = -1;
  let isLoadingRecap = true;
  let recaps: RecapProject[] = [];

  const recapService = new RecapService();

  async function createNewRecap() {
    if (!selectedVideo) return;

    Swal.fire({
      text: 'Creating recap project please wait...',
      icon: 'info'
    });

    try {
      const newRecap = await recapService.create({
        video_id: `${selectedVideo.id}`,
        recap_name: `${randomString(4)}-${selectedVideo.title}`,
        step: STEP.HIGHLIGHT_SUBTITLE
      });
      if (!newRecap || !newRecap.id || !newRecap.transcription) throw new Error('failed to create sermon recap');

      Swal.close();
      recaps.push(newRecap);
      restoreRecap(newRecap);
    } catch (error: any) {
      Swal.close();
      alert(error.toString());
      return;
    }
  }

  async function restoreRecap(data: RecapProject) {
    Swal.fire({
      text: 'Loading recap project please wait...',
      icon: 'info'
    });

    recap.set(data);
    step.update((data.step as any) || STEP.HIGHLIGHT_SUBTITLE);

    Swal.close();
  }

  async function deleteRecap(recap: RecapProject) {
    try {
      const {error} = await recapService.delete(recap.id);
      if (error) throw error;

      recaps = recaps.filter(({id}) => recap.id !== id);
    } catch (error: any) {
      alert(error.toString());
      return;
    }
  }

  function openDropDown(index: number) {
    if (openDropdownIndex !== -1) window.removeEventListener('click', closeDropdown);
    setTimeout(() => {
      openDropdownIndex = index;
      window.addEventListener('click', closeDropdown);
    });
  }

  function closeDropdown() {
    openDropdownIndex = -1;
    window.removeEventListener('click', closeDropdown);
  }

  const download = async (recap: any) => {};

  onMount(async () => {
    console.log('selected video', {selectedVideo});

    if (!selectedVideo) {
      isLoadingRecap = false;
      return;
    }

    try {
      const fetched_recaps = await recapService.fetchAll({params: {video_id: `${selectedVideo.id}`}});
      console.log('FETCHED RECAPS', fetched_recaps);
      recaps = [...fetched_recaps];
    } catch (error) {
      console.error(error);
    }

    isLoadingRecap = false;
  });

  const animationDelay = 500;
</script>

<div class="h-full w-full overflow-auto">
  <div class="flex w-full flex-wrap gap-6 p-6">
    <button on:click={() => createNewRecap()} class="flex w-[calc(33.3%-1rem)] flex-col lg:w-[calc(25%-1.5rem)]">
      <div
        class="flex h-full min-h-[9rem] w-full flex-col items-center justify-center overflow-hidden rounded-xl border border-zinc-200 hover:bg-zinc-100 dark:bg-zinc-800"
      >
        <i class="fa-solid fa-plus text-2xl"></i>
        <h1 class="truncate pt-1 text-sm text-zinc-700 dark:text-zinc-200 md:font-medium">Create New Clip</h1>
      </div>
    </button>
    {#if isLoadingRecap}
      {#each Array(parseInt((Math.random() * 11 + 3).toFixed(0))) as _, i}
        <div class="w-[calc(33.3%-1rem)] lg:w-[calc(25%-1.5rem)]">
          <div
            class="h-auto w-full animate-pulse overflow-hidden rounded-xl bg-zinc-300"
            style="animation-delay: {animationDelay * i}ms;"
          >
            <div class="aspect-video h-full w-full"></div>
          </div>
          <div class="w-full">
            <div
              class="mt-1 animate-pulse rounded-md bg-zinc-300 text-sm md:font-medium"
              style="width: {(Math.random() * 80 + 20).toFixed(0)}%; animation-delay: {animationDelay * i}ms;"
            >
              &nbsp;
            </div>
            <div
              class="mt-1 animate-pulse rounded-md bg-zinc-300 text-xs lg:text-sm"
              style="width: {(Math.random() * 90 + 10).toFixed(0)}%; animation-delay: {animationDelay * i}ms;"
            >
              &nbsp;
            </div>
          </div>
        </div>
      {/each}
    {:else if recaps.length > 0}
      {#each recaps as recap, index}
        <div class="w-[calc(33.3%-1rem)] lg:w-[calc(25%-1.5rem)]">
          <button
            on:click={() => restoreRecap(recap)}
            class="h-auto w-full overflow-hidden rounded-xl border border-transparent bg-zinc-100 hover:border-rose-500 dark:bg-zinc-700"
          >
            <img class="aspect-video w-full object-cover" src={selectedVideo?.preview} alt="video-thumbnail" />
          </button>
          <div class="relative w-full">
            <div class="flex">
              <button on:click={() => restoreRecap(recap)} class="overflow-hidden pt-1 text-start">
                <h1 class="truncate pt-1 text-sm text-zinc-700 dark:text-zinc-200 md:font-medium">
                  {recap.recap_name || 'Untitled Project'}
                </h1>
                <h2 class="truncate pt-1 text-xs text-zinc-500 lg:text-sm">{formatDate(recap.updatedAt.toString())}</h2>
              </button>
              <div class="ml-auto flex items-center px-2">
                <button class="px-2" on:click={() => openDropDown(openDropdownIndex === index ? -1 : index)}>
                  <i class="fa-solid fa-ellipsis-vertical text-xl"></i>
                </button>
              </div>
            </div>
            <div
              class="absolute z-10 w-full {openDropdownIndex === index
                ? ''
                : 'hidden'} grid rounded-lg border border-zinc-200 bg-white text-sm shadow-md"
            >
              <button on:click={() => download(recap)} class="px-4 py-2 hover:text-rose-500"> Download Recap </button>
              <button on:click={() => deleteRecap(recap)} class="px-4 py-2 hover:text-rose-500"> Delete Recap </button>
            </div>
          </div>
        </div>
      {/each}
    {/if}
  </div>
</div>
